import { atom } from "jotai";
import { atomWithImmer } from "jotai-immer";

import { formAtom } from "@frontend/lib/form";
import { ApiError } from "@frontend/lib/http";
import { loadReferences } from "@frontend/lib/references";
import type { Reference } from "@frontend/lib/references";

import { conditionsSchema } from "../schemas/conditions";
import type { ConditionsValues } from "../schemas/conditions";
import { clientApi } from "../services";
import type { PriceTypeRow } from "../services";
import type { CommercialCondition, PaymentTypeOption } from "../types";
import { toConditionsValues } from "../utils/payload";
import { clientEditorAtom, clientForm, editorTabAtom } from "./editor";

export type ConditionsOptions = {
  paymentTypes: PaymentTypeOption[];
  paymentTargets: Reference[];
  paymentPeriods: Reference[];
  billingTimes: Reference[];
  riskClasses: Reference[];
  priceTypes: PriceTypeRow[];
};

export type ConditionsState = {
  conditions: CommercialCondition | null;
  options: ConditionsOptions;
  loading: boolean;
  saving: boolean;
  conflict: boolean;
  error: string | null;
};

const emptyOptions: ConditionsOptions = {
  paymentTypes: [],
  paymentTargets: [],
  paymentPeriods: [],
  billingTimes: [],
  riskClasses: [],
  priceTypes: [],
};

export const emptyConditions = (): ConditionsValues => ({
  lockVersion: null,
  paymentTypeId: null,
  paymentEmail: null,
  paymentEmailCopyTo: null,
  paymentTargetId: null,
  subscriptionPaymentPeriodId: null,
  interventionPaymentPeriodId: null,
  subscriptionBillingTimeId: null,
  riskClassId: null,
  ongoingAuthorized: null,
  priceRevisionActivation: true,
  prices: [],
  emailRequiredFor: [],
});

export const initialConditionsState: ConditionsState = {
  conditions: null,
  options: emptyOptions,
  loading: true,
  saving: false,
  conflict: false,
  error: null,
};

export const conditionsForm = formAtom(conditionsSchema, emptyConditions(), {
  normalizeNull: false,
});

export const conditionsAtom = atomWithImmer<ConditionsState>(
  initialConditionsState,
);

export const conditionsOptionsAtom = atom((get) => get(conditionsAtom).options);

/**
 * The terms are read when the tab that shows them opens, not with the client:
 * the detail of a client is read far more often than they are.
 */
export const loadConditionsAtom = atom(
  null,
  async (get, set, clientId: number): Promise<void> => {
    if (null !== get(conditionsAtom).conditions) {
      return;
    }

    set(conditionsAtom, (state) => {
      state.loading = true;
      state.error = null;
    });

    try {
      const [
        conditions,
        paymentTypes,
        paymentTargets,
        paymentPeriods,
        billingTimes,
        riskClasses,
        priceTypes,
      ] = await Promise.all([
        clientApi.fetchConditions(clientId),
        clientApi.fetchPaymentTypes(),
        loadReferences("paymentTargets"),
        loadReferences("paymentPeriods"),
        loadReferences("subscriptionBillingTimes"),
        loadReferences("riskClasses"),
        clientApi.fetchPriceTypes(),
      ]);

      set(
        conditionsForm.actions.setInitial,
        toConditionsValues(conditions, emailRequiredFor(paymentTypes)),
      );

      set(conditionsAtom, (state) => {
        state.conditions = conditions;
        state.options = {
          paymentTypes,
          paymentTargets,
          paymentPeriods,
          billingTimes,
          riskClasses,
          priceTypes,
        };
        state.loading = false;
      });
    } catch (error: unknown) {
      set(conditionsAtom, (state) => {
        state.loading = false;
        state.error = message(error);
      });
    }
  },
);

export const saveConditionsAtom = atom(
  null,
  async (get, set, clientId: number): Promise<boolean> => {
    set(conditionsAtom, (state) => {
      state.error = null;
      state.conflict = false;
      state.saving = true;
    });

    try {
      const result = await set(
        conditionsForm.actions.submit,
        async (values) => {
          const saved = await clientApi.saveConditions(clientId, values);

          set(
            conditionsForm.actions.setInitial,
            toConditionsValues(saved, values.emailRequiredFor),
          );

          set(conditionsAtom, (state) => {
            state.conditions = saved;
          });

          set(clientForm.actions.setInitial, {
            ...get(clientForm.state.values),
            lockVersion: saved.lockVersion,
          });

          set(clientEditorAtom, (state) => {
            if (null !== state.client) {
              state.client.lockVersion = saved.lockVersion;
            }
          });
        },
      );

      if (!result.success) {
        set(editorTabAtom, "conditions");
      }

      return result.success;
    } catch (error: unknown) {
      set(conditionsAtom, (state) => {
        state.error = message(error);
        state.conflict = error instanceof ApiError && 409 === error.status;
      });

      if (error instanceof ApiError && 0 < error.violations.length) {
        set(conditionsForm.actions.setErrors, error.violations);
        set(editorTabAtom, "conditions");
      }

      return false;
    } finally {
      set(conditionsAtom, (state) => {
        state.saving = false;
      });
    }
  },
);

export const saveConditionsIfDirtyAtom = atom(
  null,
  async (get, set, clientId: number): Promise<boolean> => {
    const untouched =
      null === get(conditionsAtom).conditions ||
      !get(conditionsForm.state.isDirty);

    if (untouched) {
      return true;
    }

    set(
      conditionsForm.fields.lockVersion.atom,
      get(clientEditorAtom).client?.lockVersion ?? null,
    );

    return await set(saveConditionsAtom, clientId);
  },
);

export const resetConditionsAtom = atom(null, (_get, set): void => {
  set(conditionsAtom, () => initialConditionsState);
  set(conditionsForm.actions.setInitial, emptyConditions());
});

function emailRequiredFor(paymentTypes: PaymentTypeOption[]): number[] {
  return paymentTypes
    .filter((paymentType) => paymentType.needsEmail)
    .map((paymentType) => paymentType.id);
}

function message(error: unknown): string {
  return error instanceof Error ? error.message : String(error);
}
